/**
 * VoiceNoteWidget — TASK-476.
 * RM dictates a voice note about a client → transcribe → edit transcript → structure into
 * a CRM note draft + DNA proposals + follow-up tasks → review → commit. Nothing persists
 * until the RM commits (Laws L1/L3). Reuses ExtractionProposal for DNA review.
 */
import { useCallback, useRef, useState } from 'react';
import { Mic, Square, Loader2, Check, FileText, Camera } from 'lucide-react';

import { useAudioRecorder } from '../../hooks/useAudioRecorder';
import {
  transcribeVoiceNote,
  structureVoiceNote,
  structurePhotoNote,
  documentMeetingByDictation,
  type StructureResult,
  type VoiceNoteTask,
} from '../../services/voiceNoteService';
import { clientService } from '../../services/clientService';
import { approveFields } from '../../services/knowledgeGraphService';
import ExtractionProposal, {
  type ProposalItem,
} from '../kyc/workspace/ExtractionProposal';

type Phase =
  | 'idle'
  | 'recording'
  | 'transcribing'
  | 'transcript'
  | 'structuring'
  | 'review'
  | 'committing'
  | 'done';

interface VoiceNoteWidgetProps {
  clientId: string;
  clientName?: string;
  /** TASK-668 — bind this dictation to a meeting. Omit for a standalone client note. */
  sessionId?: string;
}

const PRIORITIES = new Set(['low', 'medium', 'high', 'critical']);
const normPriority = (p?: string): 'low' | 'medium' | 'high' | 'critical' =>
  p && PRIORITIES.has(p) ? (p as 'low' | 'medium' | 'high' | 'critical') : 'medium';

export default function VoiceNoteWidget({ clientId, clientName, sessionId }: VoiceNoteWidgetProps) {
  const recorder = useAudioRecorder();
  const [phase, setPhase] = useState<Phase>('idle');
  const [error, setError] = useState<string | null>(null);

  const [transcript, setTranscript] = useState('');
  const [language, setLanguage] = useState('en');
  const [result, setResult] = useState<StructureResult | null>(null);
  // Note write is the non-idempotent commit leg (createNote 409s on a duplicate
  // source_id). Track it so a retry after a DNA failure resumes instead of stranding.
  const noteSavedRef = useRef(false);
  const [summary, setSummary] = useState('');
  const [content, setContent] = useState('');

  // Approval state: DNA proposals keyed by field_path; tasks by index.
  const [approvedDna, setApprovedDna] = useState<Map<string, ProposalItem>>(new Map());
  const [checkedTasks, setCheckedTasks] = useState<Set<number>>(new Set());

  const fail = (e: unknown, fallback: string) =>
    setError(e instanceof Error ? e.message : fallback);

  const startRecording = useCallback(async () => {
    setError(null);
    if (await recorder.start()) setPhase('recording');
    // else: start() set recorder.error and stayed idle — don't enter the recording phase.
  }, [recorder]);

  const stopRecording = useCallback(async () => {
    try {
      const blob = await recorder.stop();
      setPhase('transcribing');
      const { transcript: t, language: lang } = await transcribeVoiceNote(blob);
      setTranscript(t);
      setLanguage(lang || 'en'); // structuring skills read ctx.language; keep the dictated tongue
      setPhase('transcript');
    } catch (e) {
      fail(e, 'Transcription failed');
      setPhase('idle');
    }
  }, [recorder]);

  const structure = useCallback(async () => {
    setError(null);
    setPhase('structuring');
    try {
      const r = await structureVoiceNote(clientId, transcript, clientName, language, sessionId);
      noteSavedRef.current = false; // fresh structuring → a new note_id, nothing saved yet
      setResult(r);
      setSummary(r.note_draft.summary);
      // Note body defaults to the verbatim dictation (a more useful CRM note than the exec
      // summary), freely editable. DNA provenance is anchored to each proposal's verbatim
      // `quote` against the immutable structured transcript — NOT to this editable body — so
      // editing it here can't invalidate the committed spans.
      setContent(transcript);
      // DNA starts un-approved: the RM opts in per item via ExtractionProposal (L1/L3
      // per-item review). includeDna/includeAllDna populate this map on Apply.
      setApprovedDna(new Map());
      setCheckedTasks(new Set(r.tasks.map((_, i) => i)));
      setPhase('review');
    } catch (e) {
      fail(e, 'Structuring failed');
      setPhase('transcript');
    }
  }, [clientId, transcript, clientName, language, sessionId]);

  /** TASK-689 (review fix) — a photographed handwritten note, structured by the same skill
   *  fan-out a dictation uses. An unreadable image says so and changes nothing: no
   *  proposals, no note draft, no half-populated review screen to talk the RM into
   *  accepting an OCR guess. */
  const structureFromPhoto = useCallback(async (file: File) => {
    setError(null);
    setPhase('structuring');
    try {
      const r = await structurePhotoNote(clientId, file, clientName, language, sessionId);
      if (r.readable === false || !r.note_draft) {
        setError(r.reason || 'That photo could not be read. Try a sharper, straighter shot.');
        setPhase('idle');
        return;
      }
      noteSavedRef.current = false;
      setResult(r);
      setSummary(r.note_draft.summary);
      // The "transcript" here is what the vision model read off the paper. Editable, and
      // shown, so the RM can correct a misread word BEFORE any of it is approved.
      // The OCR transcript, not the summary: the RM edits what the model READ, so a
      // misread word is corrected before anything is approved.
      setTranscript(r.transcript ?? '');
      setContent(r.transcript ?? '');
      setApprovedDna(new Map());
      setCheckedTasks(new Set(r.tasks.map((_, i) => i)));
      setPhase('review');
    } catch (e) {
      fail(e, 'Could not read that photo');
      setPhase('idle');
    }
  }, [clientId, clientName, language, sessionId]);

  const includeDna = (fieldPath: string) => {
    const p = result?.dna_proposals.find((x) => x.field_path === fieldPath);
    if (p) setApprovedDna((m) => new Map(m).set(fieldPath, p));
  };
  const includeAllDna = () =>
    setApprovedDna(new Map((result?.dna_proposals ?? []).map((p) => [p.field_path, p])));
  const dropDna = (fieldPath: string) =>
    setApprovedDna((m) => {
      const next = new Map(m);
      next.delete(fieldPath);
      return next;
    });

  const toggleTask = (i: number) =>
    setCheckedTasks((s) => {
      const next = new Set(s);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });

  const commit = useCallback(async () => {
    if (!result) return;
    setError(null);
    setPhase('committing');
    try {
      const inlineTasks = result.tasks
        .filter((_, i) => checkedTasks.has(i))
        .map((t: VoiceNoteTask) => ({
          title: t.title,
          due_date: t.due_date ?? undefined,
          priority: normPriority(t.priority),
          // TASK-701: the extractor's classification was dropped here, so every note-derived
          // task landed task_type = NULL. Forwarded verbatim and never invented — when the
          // server sent none, the server's own default applies rather than a UI guess.
          ...(t.task_type ? { task_type: t.task_type } : {}),
        }));

      // Note + follow-up tasks in one call (source_id links back to the dictation).
      // Skip on retry: the note is non-idempotent (createNote 409s on a duplicate
      // source_id), so once it's saved a re-commit must only finish the DNA leg.
      if (!noteSavedRef.current) {
        try {
          await clientService.createNote(clientId, {
            date: new Date().toISOString(),
            source_type: 'advisor',
            // TASK-668: a dictated meeting record is RM-authored summary evidence. The
            // label is prefixed onto the stored note so provenance survives outside this UI.
            summary: summary || (sessionId ? 'Dictated meeting summary' : 'Voice note'),
            content: sessionId && result.provenance_label
              ? `${result.provenance_label}\n\n${content}`
              : content,
            source_id: result.note_id,
            topics: result.note_draft.topics,
            inline_tasks: inlineTasks,
            // Session binding travels with the note object (notes are a JSONB array on
            // clients.metadata — no migration needed for this link, see migration 193).
            ...(sessionId ? { session_id: sessionId, note_source_type: 'rm_dictation' } : {}),
          } as Parameters<typeof clientService.createNote>[1]);
        } catch (e) {
          // 409 = this note_id already landed (e.g. a prior partial commit). Treat as
          // saved and fall through to the DNA leg rather than stranding the review.
          if ((e as { response?: { status?: number } })?.response?.status !== 409) throw e;
        }
        noteSavedRef.current = true;
      }

      // Approved DNA → upsert + verify (the human-verify gate, AC of TASK-474).
      const dnaFields = [...approvedDna.values()].map((p) => ({
        field_path: p.field_path,
        value: p.value,
        confidence: p.confidence,
        source: 'voice_note',
      }));
      if (dnaFields.length) await approveFields(clientId, dnaFields);

      // TASK-668: the meeting becomes "documented" only HERE — after an approved record
      // actually landed. Generation never sets it. Failure to mark is non-fatal: the note
      // and DNA are already committed, and re-approving is idempotent server-side.
      if (sessionId) {
        try {
          // TASK-704: the approved material travels WITH the approval. Before this the
          // endpoint received three ids and none of the content it was meant to persist, so
          // the FINSA notes and the dictated text were dropped when this component unmounted.
          // `content` is the RM-edited text — the record is what they approved, not what the
          // model first produced.
          await documentMeetingByDictation(sessionId, clientId, result.note_id, {
            transcript_text: content,
            provenance_label: result.provenance_label ?? null,
            finsa_notes: result.finsa_notes,
          });
        } catch {
          setError('The note was saved, but the meeting could not be marked as documented. Try approving again.');
        }
      }

      setPhase('done');
    } catch (e) {
      fail(e, 'Commit failed');
      setPhase('review');
    }
  }, [result, clientId, summary, content, approvedDna, checkedTasks, sessionId]);

  const reset = () => {
    recorder.cancel(); // release the mic if Cancel was hit mid-recording (no-op otherwise)
    noteSavedRef.current = false;
    setPhase('idle');
    setError(null);
    setTranscript('');
    setResult(null);
    setApprovedDna(new Map());
    setCheckedTasks(new Set());
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-kielis-navy">Voice note</h3>
        {phase !== 'idle' && phase !== 'done' && (
          <button onClick={reset} className="text-sm text-gray-500 hover:text-gray-700">
            Cancel
          </button>
        )}
      </div>

      {error && (
        <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      {(phase === 'idle' || phase === 'recording') && (
        <div className="flex flex-col items-center gap-3 py-8">
          {phase === 'idle' ? (
            <button
              onClick={startRecording}
              className="flex items-center gap-2 rounded-full bg-kielis-gold px-6 py-3 font-medium text-kielis-navy hover:opacity-90"
            >
              <Mic className="h-5 w-5" /> Start recording
            </button>
          ) : (
            <button
              onClick={stopRecording}
              className="flex items-center gap-2 rounded-full bg-red-600 px-6 py-3 font-medium text-white hover:opacity-90"
            >
              <Square className="h-5 w-5" /> Stop &amp; transcribe
            </button>
          )}
          {phase === 'recording' && (
            <span className="flex items-center gap-2 text-sm text-red-600">
              <span className="h-2 w-2 animate-pulse rounded-full bg-red-600" /> Recording…
            </span>
          )}
          {/* TASK-689 (review fix): the photo path shipped as a backend endpoint with NO
              caller, so "a fact captured on paper never reaches the graph" was still true.
              It lands in the SAME review phase as a dictation — nothing is persisted until
              the RM approves each proposal. */}
          {phase === 'idle' && (
            <label className="flex cursor-pointer items-center gap-2 text-sm text-gray-600 hover:text-kielis-navy">
              <Camera className="h-4 w-4" />
              <span>…or photograph a handwritten note</span>
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                data-testid="photo-note-input"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = ''; // let the same file be re-picked after a failure
                  if (file) void structureFromPhoto(file);
                }}
              />
            </label>
          )}
          {recorder.error && <span className="text-sm text-red-600">{recorder.error}</span>}
        </div>
      )}

      {(phase === 'transcribing' || phase === 'structuring' || phase === 'committing') && (
        <div className="flex items-center justify-center gap-2 py-8 text-gray-600">
          <Loader2 className="h-5 w-5 animate-spin" />
          {phase === 'transcribing' && 'Transcribing…'}
          {phase === 'structuring' && 'Structuring note…'}
          {phase === 'committing' && 'Saving…'}
        </div>
      )}

      {phase === 'transcript' && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700">Transcript (edit if needed)</label>
          <textarea
            value={transcript}
            onChange={(e) => setTranscript(e.target.value)}
            rows={6}
            className="w-full rounded-md border border-gray-300 p-3 text-sm"
          />
          <button
            onClick={structure}
            disabled={!transcript.trim()}
            className="rounded-md bg-kielis-navy px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            Structure note
          </button>
        </div>
      )}

      {phase === 'review' && result && (
        <div className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">CRM note</label>
            <input
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              maxLength={500}
              placeholder="Summary"
              className="w-full rounded-md border border-gray-300 p-2 text-sm font-medium"
            />
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              placeholder="Details"
              className="w-full rounded-md border border-gray-300 p-3 text-sm"
            />
          </div>

          {result.dna_proposals.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">DNA updates</label>
              <ExtractionProposal
                proposals={result.dna_proposals}
                onApply={includeDna}
                onApplyAll={includeAllDna}
                onSkip={dropDna}
              />
            </div>
          )}

          {result.tasks.length > 0 && (
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Follow-up tasks</label>
              <ul className="space-y-1">
                {result.tasks.map((t, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={checkedTasks.has(i)}
                      onChange={() => toggleTask(i)}
                    />
                    <span>{t.title}</span>
                    <span className="text-xs text-gray-400">{normPriority(t.priority)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <button
            onClick={commit}
            className="flex items-center gap-2 rounded-md bg-kielis-gold px-5 py-2 font-medium text-kielis-navy hover:opacity-90"
          >
            <FileText className="h-4 w-4" /> Commit note
            {approvedDna.size > 0 && ` + ${approvedDna.size} DNA`}
            {checkedTasks.size > 0 && ` + ${checkedTasks.size} task${checkedTasks.size > 1 ? 's' : ''}`}
          </button>
        </div>
      )}

      {phase === 'done' && (
        <div className="flex flex-col items-center gap-3 py-8">
          <span className="flex items-center gap-2 font-medium text-green-700">
            <Check className="h-5 w-5" /> Saved
          </span>
          <button
            onClick={reset}
            className="rounded-md bg-kielis-navy px-4 py-2 text-sm font-medium text-white"
          >
            New voice note
          </button>
        </div>
      )}
    </div>
  );
}
